import { Card, CardContent, CardHeader, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { capturePHEvents } from '../../analytics/posthog.analytics';
import palette from '../../theme/palette';
import TickIcon from '../../svg/TickIcon';
import { createTestForClass, updateTestById } from '../../service/test.service';
import { ConvertToBase64 } from '../../helper/commonHelper';
import { uploadFile } from '../../service/upload.file.service';
import { PH_NEW_TEST_CREATED } from '../../utils/posthog.eventNames';

const useStyles = makeStyles({
  textStyle: {
    margin: '10px 0',
    '@media (max-width: 599px)': {
      fontSize: '0.9rem'
    }
  },
  iconStyle: {
    marginRight: 15
  }
});

const TestPreview = forwardRef(({ testObj, onClose, editObj, setTestSubmitting }, ref) => {
  const classes = useStyles();
  const classData = useSelector((state) => state.classes.selectedClass);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { enqueueSnackbar } = useSnackbar();

  const {
    testName,
    marking,
    duration,
    startDate,
    startTime,
    isPracticeTest,
    isPublishResult,
    isRandomize,
    testType,
    createdAt
  } = testObj;

  const createNewTest = async (testObj) => {
    const res = await createTestForClass(classData.id, testObj);
    if (res) {
      onClose();
      enqueueSnackbar('Test Created!', {
        variant: 'success'
      });
      capturePHEvents(PH_NEW_TEST_CREATED, {
        testId: res?.id ?? res?._id,
        classId: classData.id,
        testName
      });
      setTestSubmitting(false);
    }
    setTestSubmitting(false);
  };

  const updateExistingTest = async (updatedObj) => {
    const res = await updateTestById(editObj.id, updatedObj);
    if (res) {
      onClose();
      enqueueSnackbar('Test Updated!', {
        variant: 'success'
      });
      setTestSubmitting(false);
    }
    setTestSubmitting(false);
  };

  const getModifiedTestObj = () => {
    const newTestObj = {
      name: testName,
      type: testType.toLowerCase(),
      correctMark: marking.correct,
      totalMark: marking.total,
      duration,
      startDate: new Date(startDate).toISOString(),
      startTime: new Date(startTime).toISOString(),
      isPractice: isPracticeTest,
      incorrectMark: marking.incorrect,
      isRandomize,
      isPublishResult,
      isTestPublish: true
    };
    return newTestObj;
  };

  const updateTest = () => {
    setTestSubmitting(true);
    const newTestObj = getModifiedTestObj();
    if (testObj.description) newTestObj.description = testObj.description;
    updateOrCreateTest(newTestObj);
  };

  const handleTestUploadType = (newTestObj) => {
    // eslint-disable-next-line
    if (testObj.hasOwnProperty('questions')) {
      if (testObj?.questions.length > 0) {
        let answerSheet = [];
        answerSheet = testObj?.questions.map((q) => ({
          questionNumber: q.questionNumber,
          options: q.options.map((op) => ({
            option: op.option,
            isAnswer: op.isAnswer
          }))
        }));
        newTestObj.answerSheet = answerSheet;
        newTestObj.totalQuestions = testObj.numOfQuestions;
        newTestObj.optionsPerQuestion = testObj.numOfOptions;
      }
    }
    if (editObj) {
      updateExistingTest(newTestObj);
    } else {
      createNewTest(newTestObj);
    }
  };

  const handleFileUpload = async (newTestObj) => {
    const base64 = await ConvertToBase64(testObj.testFile);
    const fileObj = {
      base64,
      fileType: testObj.testFile.type,
      fileName: testObj.testFile.name,
      uploadFor: 'test',
      userId: currentUser.id,
      orgId: classData.orgID
    };
    const fileRes = await uploadFile(classData.id, fileObj);
    if (fileRes) {
      newTestObj.typeURL = fileRes.fileURL;
      handleTestUploadType(newTestObj);
    } else {
      enqueueSnackbar('Please try again later', {
        variant: 'error'
      });
    }
  };

  const updateOrCreateTest = async (newTestObj) => {
    if (testObj.testType.toLowerCase() === 'upload') {
      // eslint-disable-next-line
      if (testObj.testFile.type) {
        // We have an file to upload
        handleFileUpload(newTestObj);
      } else {
        handleTestUploadType(newTestObj);
      }
    }
    if (testObj.testType.toLowerCase() === 'type') {
      const updatedQuesArray = testObj.questions.map((qu) => ({
        name: qu.name,
        // TODO: when image is added then we have to make req to get storage url
        // image: '',
        options: qu.options.map((op) => ({
          value: op.value,
          isAnswer: op.isAnswer
        }))
      }));
      newTestObj.questions = updatedQuesArray;
      if (editObj) {
        updateExistingTest(newTestObj);
      } else {
        createNewTest(newTestObj);
      }
    }
  };

  const createTest = async () => {
    setTestSubmitting(true);
    const newTestObj = getModifiedTestObj();
    newTestObj.orgId = classData.orgId._id;
    if (testObj.description) newTestObj.description = testObj.description;
    updateOrCreateTest(newTestObj);
  };

  useImperativeHandle(ref, () => ({
    handleSubmitTest() {
      if (editObj) {
        updateTest();
      } else {
        createTest();
      }
    }
  }));

  return (
    <div style={{ marginTop: 20 }}>
      <Card>
        <CardHeader
          title={testName}
          sx={{
            borderBottom: `1px solid ${palette.grey[300]}`,
            padding: '15px 24px'
          }}
        />
        <CardContent>
          <Typography className={classes.textStyle}>
            <TickIcon
              className={classes.iconStyle}
              color={palette.primary.main}
              height={15}
              width={15}
            />
            Created on : {moment(createdAt).format('DD MMM, LT')}
          </Typography>
          <Typography className={classes.textStyle}>
            <TickIcon
              className={classes.iconStyle}
              color={palette.primary.main}
              height={15}
              width={15}
            />
            Start time :{' '}
            <span style={{ color: palette.secondary.main }}>
              {moment(startTime).format('DD MMM, LT')}
            </span>
          </Typography>
          <Typography className={classes.textStyle}>
            <TickIcon
              className={classes.iconStyle}
              color={palette.primary.main}
              height={15}
              width={15}
            />
            Duration : <span style={{ color: palette.secondary.main }}>{duration} min</span>
          </Typography>
          <Typography className={classes.textStyle}>
            <TickIcon
              className={classes.iconStyle}
              color={palette.primary.main}
              height={15}
              width={15}
            />
            End Time : {moment(startTime).add(duration, 'minutes').format('DD MMM, LT')}
          </Typography>
          {testObj?.questions && (
            <Typography className={classes.textStyle}>
              <TickIcon
                className={classes.iconStyle}
                color={palette.primary.main}
                height={15}
                width={15}
              />
              Questions : {testObj?.questions.length}
            </Typography>
          )}
          {isPracticeTest && (
            <Typography className={classes.textStyle}>
              <TickIcon
                className={classes.iconStyle}
                color={palette.primary.main}
                height={15}
                width={15}
              />
              Practice Test: Yes
            </Typography>
          )}
          {isPublishResult && (
            <Typography className={classes.textStyle}>
              <TickIcon
                className={classes.iconStyle}
                color={palette.primary.main}
                height={15}
                width={15}
              />
              Publish Result: Yes
            </Typography>
          )}
          {isRandomize && (
            <Typography className={classes.textStyle}>
              <TickIcon
                className={classes.iconStyle}
                color={palette.primary.main}
                height={15}
                width={15}
              />
              Randomized : Yes
            </Typography>
          )}
          <Typography className={classes.textStyle}>
            <TickIcon
              className={classes.iconStyle}
              color={palette.primary.main}
              height={15}
              width={15}
            />
            Max Marks : {marking.total}
          </Typography>
          <Typography className={classes.textStyle}>
            <TickIcon
              className={classes.iconStyle}
              color={palette.primary.main}
              height={15}
              width={15}
            />
            Correct Ans : <span style={{ color: 'green' }}>+{marking.correct}</span>
          </Typography>
          {marking.incorrect !== 0 && (
            <Typography className={classes.textStyle}>
              <TickIcon
                className={classes.iconStyle}
                color={palette.primary.main}
                height={15}
                width={15}
              />
              Incorrect Ans : <span style={{ color: 'red' }}>-{marking.incorrect}</span>
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

TestPreview.propTypes = {
  testObj: PropTypes.shape({
    testName: PropTypes.string,
    description: PropTypes.string,
    testFile: PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string
    }),
    marking: PropTypes.shape({
      correct: PropTypes.number,
      incorrect: PropTypes.number,
      total: PropTypes.number
    }),
    duration: PropTypes.any,
    startDate: PropTypes.any,
    startTime: PropTypes.any,
    isPracticeTest: PropTypes.bool,
    isPublishResult: PropTypes.bool,
    isRandomize: PropTypes.bool,
    questions: PropTypes.array,
    testType: PropTypes.string,
    createdAt: PropTypes.any,
    numOfQuestions: PropTypes.number,
    numOfOptions: PropTypes.number
  }),
  setTestSubmitting: PropTypes.func,
  onClose: PropTypes.func,
  editObj: PropTypes.shape({
    id: PropTypes.string
  })
};

export default TestPreview;
